import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bar, BarChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis } from 'recharts';

import { ReactComponent as NavigateArrow } from '../../../../assets/icons/dashboard/navigate-arrow.svg';
import { chartTimePeriod } from '../../../../config/enum';
import currentTimeZone from '../../../../config/timeZone';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  fetchListFilters,
  updateScheduleFilters,
} from '../../../../redux/slices/Partners/ScheduleManagementSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import CustomToggleButton from '../../../../shared-components/custom-toggle-button/CustomToggleButton';
import {
  fillMissingLast24HrsGeneric,
  fillMissingLastNDaysGeneric,
  fillMissingMonthsGeneric,
} from '../../../../shared-functions/ChartFunctions';
import dashboard from '../../../../utils/api/partner/dashboard';

interface ChartDataProperties {
  label: string;
  [key: string]: string | number;
}

/**
 * Charging schedule chart
 * @returns {any} The result
 */
export default function ChargingScheduleChart() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [barGraphData, setBarGraphData] = useState<ChartDataProperties[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const activeRangeReference = React.useRef<
    'last24hours' | 'last7days' | 'lastMonth' | 'last12months'
  >('last24hours');
  const { filters, filterListLoading } = useAppSelector((s) => s.approvedStation);

  /**
   * Fetch power consumed data based on time range
   * @param {any} range - The range
   */
  const fetchPowerConsumedReport = async (
    range: 'last24hours' | 'last7days' | 'lastMonth' | 'last12months',
  ) => {
    activeRangeReference.current = range;
    try {
      setIsLoading(true);
      const response = await dashboard.getScheduleChargingReport({
        timePeriod: range,
        timeZone: currentTimeZone,
      });

      // ❌ If user already switched to another range, ignore this response
      if (activeRangeReference.current !== range) return;

      // ✅ Otherwise, update the chart with the right data
      if (range === 'last12months' && response.status === 200) {
        setBarGraphData(fillMissingMonthsGeneric(response.data));
      } else if (range === 'lastMonth' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 30));
      } else if (range === 'last7days' && response.status === 200) {
        setBarGraphData(fillMissingLastNDaysGeneric(response.data, 7));
      } else if (range === 'last24hours' && response.status === 200) {
        setBarGraphData(fillMissingLast24HrsGeneric(response.data));
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);

      // only stop loading if this request is still the active one
      if (activeRangeReference.current === range) {
        setIsLoading(false);
      }
    }
  };

  /**
   * Function to show custom tooltip
   * @param {any} param0 - The param0
   * @param {any} param0.active - The active prop
   * @param {any} param0.payload - The payload prop
   * @returns {any} The result
   */
  function CustomTooltip({ active, payload }: TooltipProps<number, string>) {
    if (active && payload && payload.length > 0) {
      const data = payload[0];

      return (
        <div className="charging-schedule-custom-tooltip-partner">
          <p className="label">{data?.payload?.label}</p>
          <div className="tooltip-values">
            <p style={{ color: '#3FA9F5' }}>
              {t('confirmed')}: {data?.payload?.confirmedCount}
            </p>
            <p style={{ color: '#F04438' }}>
              {t('cancelled')}: {-(data?.payload?.canceledCount || 0)}
            </p>
          </div>
        </div>
      );
    }

    return null;
  }

  /**
   * Function to handle click event in each bar
   * @param {any} key - The key
   */
  const handleBarChartClickEvent = (key: string) => {
    dispatch(
      updateScheduleFilters({
        key: 'status',
        value: key,
        type: 'replace',
      }),
    );

    navigate('/ev-admin/schedule-management');
  };

  useEffect(() => {
    const filterWithOutSearchBy = filters.filter((eachFilter) => eachFilter.key !== 'searchBy');
    if (filterWithOutSearchBy.length === 0 && !filterListLoading) {
      dispatch(fetchListFilters());
    }
    fetchPowerConsumedReport('last24hours');
  }, []);

  return (
    <div className="charging-schedule-chart-wrap-partner">
      <div className="charging-schedule-chart-header">
        <div className="header-title">
          <h6>{t('chargingSchedules')}</h6>
          <span
            className="header-arrow"
            role="presentation"
            onClick={() => navigate('/ev-admin/schedule-management')}>
            <NavigateArrow />
          </span>
        </div>
        <CustomToggleButton
          labels={chartTimePeriod.map((duration) => t(duration.label))}
          onToggle={(index, label) => {
            const selectedDuration = chartTimePeriod.find(
              (duration) => t(duration.label) === label,
            );
            fetchPowerConsumedReport(selectedDuration?.value || 'last24hours');
            setIsLoading(true);
          }}
          defaultActiveIndex={0}
          type="chart"
        />
      </div>
      {isLoading ? (
        <center className="charging-schedule-loader">
          <BatteryLoader />
        </center>
      ) : (
        <>
          <div className="charging-schedule-chart-container">
            <ResponsiveContainer width="100%" height={275}>
              <BarChart
                data={barGraphData.map((eachData) => ({
                  ...eachData,
                  canceledCount: Number(eachData.canceledCount) > 0 ? -eachData.canceledCount : 0,
                }))}
                barCategoryGap={25}>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  fontSize={12}
                  stroke="#535353"
                />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'transparent' }} />
                {/* <YAxis
              axisLine={false}
              tickLine={false}
              domain={['dataMin - 1', 'dataMax + 1']}
            /> */}

                <Bar dataKey="canceledCount" barSize={26} radius={[20, 20, 0, 0]} fill="#F04438" />
                <Bar
                  dataKey="confirmedCount"
                  barSize={26}
                  radius={[20, 20, 0, 0]}
                  fill="#3FA9F5"
                  onClick={() => handleBarChartClickEvent('confirmed')}
                  style={{ cursor: 'pointer' }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="charging-schedule-chart-label">
            <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#3FA9F5' }} />
              <p>{t('confirmed')}</p>
            </div>
            <div className="each-label">
              <div className="each-label-color" style={{ backgroundColor: '#F04438' }} />
              <p>{t('cancelled')}</p>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
